import { SearchableSelect, ToggleSwitch } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useMemo, useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import ConditionsCard from "../../../../../components/conditionsCrad/conditionsCard";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import CallingRuleTriggersSection from "../../../../../components/modals/AddNewCallingRuleModal/CallingRuleTriggersSection";
import DeleteStepConfirmModal from "../../../../../components/modals/DeleteStepConfirmModal/deleteStepConfirmModal";
import PharmacyOrderUpdateRuleCard from "../../../../../components/pharmacyOrderUpdates/PharmacyOrderUpdateRuleCard";
import { OrganizationType } from "../../../../../enums/organizationTypes";
import {
  groupRulesByConditionBlockLayout,
  type FirstStpReviewRuleState,
} from "./firstStpReviewRuleUtils";
import { useFirstStpConfig, type StpVariant } from "./useFirstStpConfig";
import { useFirstStpReviewRules } from "./useFirstStpReviewRules";

/** Org options for STP review no-touch config. */
const FIRST_STP_ORG_OPTIONS = [
  { label: "NYCBS Pharmacy", value: OrganizationType.NYCBS_PHARMA },
  { label: "NYCBS STAT", value: OrganizationType.NYCBS_STATS },
  { label: "Astera Pharmacy", value: OrganizationType.ASTERA },
  { label: "Astera Primary Care", value: OrganizationType.ASTERA_PRIMARY_CARE },
];

/** Copy that differs between the First STP Review, Second STP Review and Agentic tabs. */
const STP_VARIANT_COPY: Record<
  StpVariant,
  { orgSelectId: string; loadingMessage: string; defaultRuleSubtext: string }
> = {
  first: {
    orgSelectId: "first-stp-org",
    loadingMessage: "Loading first STP review rules...",
    defaultRuleSubtext: "First STP review rule",
  },
  second: {
    orgSelectId: "second-stp-org",
    loadingMessage: "Loading second STP review rules...",
    defaultRuleSubtext: "Second STP review rule",
  },
  agentic: {
    orgSelectId: "agentic-org",
    loadingMessage: "Loading agentic rules...",
    defaultRuleSubtext: "Agentic rule",
  },
};

interface FirstStpLogicProps {
  /** Review stage this page edits; decides which no-touch config document is used. */
  variant?: StpVariant;
}

const FirstStpLogic = ({ variant = "first" }: FirstStpLogicProps) => {
  const copy = STP_VARIANT_COPY[variant];
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    OrganizationType.NYCBS_PHARMA,
  );
  const {
    data: firstStpDoc,
    loading: configLoading,
    error: configError,
    loadedOrgId: configLoadedOrgId,
  } = useFirstStpConfig(selectedOrgId, variant);
  const {
    rules,
    triggers,
    blockLayout,
    conditionConfig,
    configKey,
    actionFieldOptions,
    actionValueConfig,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddConditionBlock,
    handleAddRuleToBlock,
    handleBlockLabelChange,
    handleBlockSubtextChange,
    handleDeleteConditionBlock,
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
    handleDeleteRule,
    handleTriggerTypeChange,
    handleTriggerScheduleChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleSave,
    handleDiscard,
    lastSavedMeta,
    enabled,
    handleEnabledToggle,
  } = useFirstStpReviewRules(
    firstStpDoc,
    configLoading,
    selectedOrgId,
    configLoadedOrgId,
    variant,
  );

  const [pendingDeleteRuleId, setPendingDeleteRuleId] = useState<string | null>(
    null,
  );
  const [pendingDeleteBlockIndex, setPendingDeleteBlockIndex] = useState<
    number | null
  >(null);

  const groupedRules = useMemo(
    () => groupRulesByConditionBlockLayout(blockLayout, rules),
    [blockLayout, rules],
  );

  const formattedLastUpdated =
    lastSavedMeta?.updated_at != null
      ? moment(lastSavedMeta.updated_at).format("DD MMM YYYY HH:mm")
      : null;

  const renderRuleCard = (rule: FirstStpReviewRuleState) => (
    <PharmacyOrderUpdateRuleCard
      key={rule.id}
      ruleId={rule.id}
      label={rule.label}
      subtext={rule.subtext}
      active={rule.active}
      condition={rule.condition}
      actions={rule.actions}
      conditionConfig={conditionConfig}
      actionValueConfig={actionValueConfig}
      actionFieldOptions={actionFieldOptions}
      configKey={configKey}
      ruleLoadedVersion={ruleLoadedVersion}
      defaultSubtext={copy.defaultRuleSubtext}
      onLabelChange={(value) => handleRuleLabelChange(rule.id, value)}
      onSubtextChange={(value) => handleRuleSubtextChange(rule.id, value)}
      onActiveToggle={() => handleRuleActiveToggle(rule.id)}
      onConditionChange={(value) => handleRuleConditionChange(rule.id, value)}
      onActionFieldChange={(index, value) =>
        handleRuleActionFieldChange(rule.id, index, value)
      }
      onActionTypeChange={(index, value) =>
        handleRuleActionTypeChange(rule.id, index, value)
      }
      onActionStringValueChange={(index, value) =>
        handleRuleActionStringValueChange(rule.id, index, value)
      }
      onActionMultiValueChange={(index, values) =>
        handleRuleActionMultiValueChange(rule.id, index, values)
      }
      onAddAction={() => handleRuleAddAction(rule.id)}
      onRemoveAction={(index) => handleRuleRemoveAction(rule.id, index)}
      onDelete={() => setPendingDeleteRuleId(rule.id)}
    />
  );

  const renderAddRuleButton = (blockIndex: number) => (
    <div
      role="button"
      tabIndex={0}
      className="flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md border border-primaryGray-14 p-4 transition-colors hover:border-primaryGray-12 hover:bg-primaryGray-16"
      onClick={() => handleAddRuleToBlock(blockIndex)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleAddRuleToBlock(blockIndex);
        }
      }}
    >
      <div>+</div>
      <div className="text-sm">Add New Rule</div>
    </div>
  );

  const handleOrgChange = (option: { value: string }) => {
    if (
      isDirty &&
      !window.confirm(
        "You have unsaved changes. Switch organization anyway? Unsaved changes will be lost.",
      )
    ) {
      return;
    }
    setSelectedOrgId(option.value);
  };

  const orgSelector = (
    <div className="first-stp-logic-layout__org-selector mb-4 mt-2 flex w-full flex-shrink-0 flex-row items-end justify-between gap-4">
      <div className="w-full max-w-sm">
        <SearchableSelect
          key={selectedOrgId}
          id={copy.orgSelectId}
          options={FIRST_STP_ORG_OPTIONS}
          label="Organization"
          placeholder="Select Organization"
          defaultValue={selectedOrgId}
          onOptionChange={handleOrgChange}
        />
      </div>
      {variant === "second" && !configLoading && (
        <div className="mb-1 flex items-center gap-2">
          <span
            className={`text-xs font-semibold ${
              enabled ? "text-green-700" : "text-red-700"
            }`}
          >
            Save and Submit
          </span>
          <ToggleSwitch
            key={`second-stp-enabled-${selectedOrgId}-${ruleLoadedVersion}`}
            id="second-stp-enabled"
            defaultChecked={enabled}
            onChangeEmit={handleEnabledToggle}
          />
        </div>
      )}
    </div>
  );

  if (configLoading) {
    return (
      <div className="first-stp-logic__loading flex h-full min-h-0 flex-col">
        {orgSelector}
        <LoaderMessage message={copy.loadingMessage} />
      </div>
    );
  }

  if (configError != null) {
    return (
      <div className="first-stp-logic__loading flex h-full min-h-0 flex-col p-4">
        {orgSelector}
        <p className="text-body1 text-red-600" role="alert">
          {configError.message}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="first-stp-logic-layout flex min-h-0 flex-1 flex-col overflow-hidden">
        {orgSelector}
        <div className="first-stp-logic-layout__content mb-1 flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="flex flex-col gap-2 pb-4">
            {groupedRules.map(({ layout, rules: blockRules }, blockIndex) => (
              <ConditionsCard
                key={`condition-block-${blockIndex}`}
                header={layout.lable || `Condition ${blockIndex + 1}`}
                subHeader={layout.subtext}
                editableLabel={layout.lable || `Condition ${blockIndex + 1}`}
                onLabelChange={(value) =>
                  handleBlockLabelChange(blockIndex, value)
                }
                editableSubtext={layout.subtext ?? ""}
                onSubtextChange={(value) =>
                  handleBlockSubtextChange(blockIndex, value)
                }
                subtextPlaceholder="Condition description"
                headerInputKey={`block-${blockIndex}-${ruleLoadedVersion}`}
                defaultExpanded
                headerClickable={false}
                onDeleteStageClick={() =>
                  setPendingDeleteBlockIndex(blockIndex)
                }
                className="first-stp-logic-layout__condition-block rounded-md border border-primaryGray-14"
                expandableContentClassName="flex flex-col gap-2 p-2"
              >
                {blockRules.map((rule) => renderRuleCard(rule))}
                {renderAddRuleButton(blockIndex)}
              </ConditionsCard>
            ))}

            <div
              role="button"
              tabIndex={0}
              className="flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md border border-primaryGray-14 p-4 transition-colors hover:border-primaryGray-12 hover:bg-primaryGray-16"
              onClick={handleAddConditionBlock}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleAddConditionBlock();
                }
              }}
            >
              <div>+</div>
              <div className="text-sm">Add New Condition</div>
            </div>

            <CallingRuleTriggersSection
              triggers={triggers}
              loadedVersion={ruleLoadedVersion}
              onAddTrigger={handleAddTrigger}
              onRemoveTrigger={handleRemoveTrigger}
              onTypeChange={handleTriggerTypeChange}
              onScheduleChange={handleTriggerScheduleChange}
            />
          </div>
        </div>

        <div className="first-stp-logic-layout__footer flex flex-shrink-0 flex-row items-center justify-between gap-4 border-t border-primaryGray-16 bg-white py-4">
          <div className="flex flex-row gap-4 text-tiny">
            {lastSavedMeta != null &&
            (lastSavedMeta.updated_by != null ||
              lastSavedMeta.updated_at != null) ? (
              <div className="mt-1">
                Last updated:{" "}
                {formattedLastUpdated != null && (
                  <span className="font-semibold">{formattedLastUpdated}</span>
                )}
                {lastSavedMeta.updated_by != null && (
                  <>
                    {" "}
                    by{" "}
                    <span className="font-semibold">
                      {lastSavedMeta.updated_by}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-1 text-primaryGray-12">Not saved yet</div>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <Button
              buttonType="secondary"
              size="medium"
              onClick={handleDiscard}
              disabled={!isDirty}
              children="Cancel"
            />
            <Button
              buttonType="primary"
              size="medium"
              onClick={handleSave}
              disabled={!isDirty || saving}
              children={saving ? "Saving..." : "Save"}
            />
          </div>
        </div>
      </div>

      {pendingDeleteRuleId != null && (
        <DeleteStepConfirmModal
          onConfirm={() => {
            handleDeleteRule(pendingDeleteRuleId);
            setPendingDeleteRuleId(null);
          }}
          onClose={() => setPendingDeleteRuleId(null)}
        />
      )}

      {pendingDeleteBlockIndex != null && (
        <DeleteStepConfirmModal
          onConfirm={() => {
            handleDeleteConditionBlock(pendingDeleteBlockIndex);
            setPendingDeleteBlockIndex(null);
          }}
          onClose={() => setPendingDeleteBlockIndex(null)}
        />
      )}
    </div>
  );
};

export default FirstStpLogic;
